import { type ComponentType } from 'react';
import {
  LayoutDashboard, ShieldCheck, Clock, Target, Activity,
  CreditCard, Files, AlertCircle, History, FileText, Users, Info,
} from 'lucide-react';

/**
 * `PrimaryNavItem`/`PrimaryNavSubItem` live here rather than in `primary-nav.tsx` so this
 * file can be imported from `tests/` without JSX — `useCaseNavItems` is pure logic (no
 * hooks called inside it) and `tsconfig.test.json` deliberately carries no `--jsx` (tests/
 * stays DOM/JSX-free). `primary-nav.tsx` re-exports both for its existing callers.
 */
export interface PrimaryNavSubItem {
  to: string;
  label: string;
  testId: string;
}

export interface PrimaryNavItem {
  to: string;
  label: string;
  // `| undefined` explicit on both, not just `?:` — tsconfig.test.json's
  // `exactOptionalPropertyTypes` (frontend's own tsconfig doesn't set it) treats a bare
  // `className?: string` as narrower than Lucide's own prop type otherwise.
  Icon: ComponentType<{ className?: string | undefined; 'aria-hidden'?: boolean | 'true' | 'false' | undefined }>;
  testId: string;
  /** react-router NavLink `end` - only Dashboard ("/") needs exact matching. */
  end?: boolean;
  /** Sub-links shown indented beneath the parent. Rendered on BOTH surfaces
   *  (the pre-v7.100 mobile header cluster was flat; the drawer is not). */
  children?: PrimaryNavSubItem[];
  /** Section divider label rendered above the first item in each new section. */
  section?: string;
}

export function useCaseNavItems(
  caseId: string,
  isOwner: boolean,
  isClinicalMember: boolean,
  /**
   * `case.read_payers` = [orBillingAuthority(onCase), practiceIn('owner','admin',
   * 'practitioner')] — the Billing nav item's real rule, separated from `isOwner`.
   * They were one flag, so the hub card (fixed to read the capability) offered
   * Billing to a practitioner while this nav hid it — the drift §5.4 exists to prevent.
   * `orBillingAuthority` is the off-case arm: an owner/admin with no case row still gets
   * this item, via `use-nav-context.ts`'s business-view fallback.
   */
  canReadPayers: boolean,
  /** `case.read_audit`. History was unconditional, so it 403'd for everyone below that. */
  canReadAudit: boolean,
  /** `case.read_incidents`, same shape as `canReadAudit` — Incidents was unconditional too. */
  canReadIncidents: boolean,
  /** `case.read_members` — Members was unconditional; the earlier claim that Members has
   *  its own fallback, unlike Details, was true only for owners/front-office admins (an
   *  on-case member with neither membership authority nor a clinical role is refused too). */
  canReadMembers: boolean,
  /** A real `case_members` row — false for the off-case business audience.
   *  Details has no business-view fallback, so offering it here is offering a 403. */
  isCaseMember: boolean,
  /** `case.read_contract` — the Agreement item's own rule, same one the hub card reads
   *  (an `isOwner`-only nav disagreed with the card, which a non-owner
   *  business-authority holder or the case's own client_contact now passes). */
  canReadContract: boolean,
): PrimaryNavItem[] {
  if (!caseId) return [];
  const base = `/cases/${caseId}`;
  const caseInfoSection = isOwner ? 'Admin' : 'Info';
  const items: PrimaryNavItem[] = [
    { to: base, label: 'Case overview', Icon: LayoutDashboard, testId: 'case-nav-overview', end: true },
  ];
  if (isClinicalMember) {
    items.push(
      // SESSIONS FIRST — the record of what actually happened. It had no nav entry at all
      // and was reachable only from a hub card, while the only "Sessions" anywhere in the
      // chrome was /profile/sessions, which means LOGIN sessions. One word, two meanings,
      // and the nav exposed the one that is not clinical.
      { to: `${base}/sessions`, label: 'Sessions', Icon: Clock, testId: 'case-nav-sessions', section: 'Clinical' },
      { to: `${base}/skill-targets`, label: 'Skill targets', Icon: Target, testId: 'case-nav-skill-targets', section: 'Clinical' },
      { to: `${base}/behaviour-targets`, label: 'Behaviour targets', Icon: Activity, testId: 'case-nav-behaviour-targets', section: 'Clinical' },
    );
  }
  if (isCaseMember) {
    items.push({ to: `${base}/details`, label: 'Details', Icon: Info, testId: 'case-nav-details', section: caseInfoSection });
  }
  if (canReadMembers) {
    items.push({ to: `${base}/members`, label: 'Members', Icon: Users, testId: 'case-nav-members', section: caseInfoSection });
  }
  if (isClinicalMember) {
    items.push({ to: `${base}/documents`, label: 'Documents', Icon: Files, testId: 'case-nav-documents', section: caseInfoSection });
  }
  if (canReadIncidents) {
    items.push({ to: `${base}/incidents`, label: 'Incidents', Icon: AlertCircle, testId: 'case-nav-incidents', section: caseInfoSection });
  }
  if (canReadAudit) {
    items.push({ to: `${base}/history`, label: 'History', Icon: History, testId: 'case-nav-history', section: caseInfoSection });
  }
  // §8.4 governance: who holds clinical step-up access to this case, and the family's
  // objections. Owner-only per SPEC §5 (a non-owner sees only their own grant), and it was
  // reachable ONLY by typing the URL or following a notification.
  if (isOwner) {
    items.push({ to: `${base}/access-register`, label: 'Record access', Icon: ShieldCheck, testId: 'case-nav-access-register', section: 'Admin' });
  }
  if (canReadContract) {
    items.push({ to: `${base}/agreement`, label: 'Agreement', Icon: FileText, testId: 'case-nav-agreement', section: 'Admin' });
  }
  if (canReadPayers) {
    items.push({ to: `${base}/invoicing`, label: 'Invoicing', Icon: CreditCard, testId: 'case-nav-billing', section: 'Admin' });
  }
  return items;
}
